import { format, parseISO, eachDayOfInterval, addDays, startOfWeek, isSameDay } from "date-fns";
import { Lock } from "lucide-react";

/**
 * NSW Police HWP-style roster layout.
 * Renders the full date range as fortnight (14-day) blocks. Each block has:
 *  - A header band (team / fortnight label / week-commencing dates)
 *  - A "Day & Date" row across 7 columns × 2 weeks
 *  - An "Operations" strip showing on-demand / global shifts
 *  - Per-employee rows split into AM (top) and PM (bottom) sub-rows
 *  - A right-side "# rest days" tally column
 *  - A bottom "AM" + "PM" daily-tally row
 *
 * Cell semantics:
 *  - shift.start_time hour < 12 → AM half
 *  - else → PM half
 *  - approved leave during the date → "A/L" pill (purple)
 *  - empty AM and PM → "REST" pill (yellow)
 */
const PoliceRosterView = ({ roster, employees, shifts, timeOff = [], teamName = "" }) => {
  if (!roster?.start_date || !roster?.end_date) return null;

  const allDates = eachDayOfInterval({
    start: parseISO(roster.start_date),
    end: parseISO(roster.end_date),
  });

  // Group dates into fortnights aligned to weeks (Sun start).
  // Pad the first fortnight with leading empty cells so the calendar starts on Sunday.
  const fortnights = [];
  if (allDates.length > 0) {
    const first = allDates[0];
    const firstSunday = startOfWeek(first, { weekStartsOn: 0 });
    let cursor = firstSunday;
    while (cursor <= allDates[allDates.length - 1]) {
      const block = [];
      for (let i = 0; i < 14; i++) {
        block.push(addDays(cursor, i));
      }
      fortnights.push(block);
      cursor = addDays(cursor, 14);
    }
  }

  const shiftMap = Object.fromEntries(shifts.map((s) => [s.shift_id, s]));
  const empMap = Object.fromEntries(employees.map((e) => [e.employee_id, e]));

  const isInRoster = (d) => {
    const ds = format(d, "yyyy-MM-dd");
    return ds >= roster.start_date && ds <= roster.end_date;
  };

  const onLeave = (employee_id, dateStr) => {
    return timeOff.some(
      (t) =>
        t.employee_id === employee_id &&
        t.status === "approved" &&
        t.start_date <= dateStr &&
        t.end_date >= dateStr
    );
  };

  const cellEntries = (employee_id, dateStr) =>
    (roster.entries || []).filter(
      (e) => e.employee_id === employee_id && e.date === dateStr
    );

  const splitAmPm = (entries) => {
    const am = [];
    const pm = [];
    entries.forEach((e) => {
      const sh = shiftMap[e.shift_id];
      if (!sh) return;
      const hour = parseInt((sh.start_time || "00:00").split(":")[0], 10);
      if (hour < 12) am.push({ entry: e, shift: sh });
      else pm.push({ entry: e, shift: sh });
    });
    return { am, pm };
  };

  // Build operations strip — entries for shifts whose frequency is on_demand
  // or whose name contains 'OPERATION' / 'EVENT'. Render as date-spanning bars.
  const operationShiftIds = new Set(
    shifts
      .filter(
        (s) =>
          s.frequency === "on_demand" ||
          /operation|event|fum|sport|crisis|training/i.test(s.name)
      )
      .map((s) => s.shift_id)
  );

  const restAbbrevColor = "#FFCC00";
  const leaveColor = "#A78BFA"; // purple-300 for A/L

  const halfCellLabel = (half) => {
    if (!half.length) return null;
    const { shift } = half[0];
    const hour = parseInt((shift.start_time || "00:00").split(":")[0], 10);
    // Short label: just the hour (e.g. 6AM, 9PM, 4PM). Falls back to shift name first chars.
    const sh12 = ((hour + 11) % 12) + 1;
    const suffix = hour < 12 ? "AM" : "PM";
    return `${sh12}${suffix}`;
  };

  const halfTitle = (half) => {
    if (!half.length) return "";
    const { shift } = half[0];
    return `${shift.name} ${shift.start_time}-${shift.end_time}`;
  };

  const halfBgColor = (half, fallback = "#F4F4F5") => {
    if (!half.length) return fallback;
    return (half[0].shift.color || "#002FA7") + "33"; // 20% alpha
  };

  const halfBorder = (half) => {
    if (!half.length) return "transparent";
    return half[0].shift.color || "#002FA7";
  };

  const dayLabel = (d) => format(d, "EEE").toUpperCase();
  const dateLabel = (d) => format(d, "d-MMM");

  return (
    <div className="space-y-8" data-testid="police-roster-view">
      {fortnights.map((block, fIdx) => {
        const blockStart = block[0];
        const blockEnd = block[13];
        const visibleDays = block.filter(isInRoster);
        if (visibleDays.length === 0) return null;

        // Operations on each date in this fortnight
        const opsByDate = block.map((d) => {
          const ds = format(d, "yyyy-MM-dd");
          if (!isInRoster(d)) return [];
          return (roster.entries || [])
            .filter(
              (en) => en.date === ds && operationShiftIds.has(en.shift_id)
            )
            .map((en) => ({ entry: en, shift: shiftMap[en.shift_id] }))
            .filter((x) => x.shift);
        });
        // Deduplicate operations by shift name on each date for a cleaner strip.
        const dedupedOps = opsByDate.map((arr) => {
          const seen = new Set();
          return arr.filter((x) => {
            if (seen.has(x.shift.shift_id)) return false;
            seen.add(x.shift.shift_id);
            return true;
          });
        });
        const hasAnyOps = dedupedOps.some((a) => a.length > 0);

        return (
          <div
            key={fIdx}
            className="border border-zinc-300 dark:border-zinc-700 overflow-hidden"
            data-testid={`police-fortnight-${fIdx}`}
          >
            {/* Title band */}
            <div className="bg-[#002FA7] text-white px-4 py-2 flex items-center justify-between flex-wrap gap-2">
              <div className="text-sm font-bold tracking-wide uppercase">
                {teamName ? `${teamName} ` : ""}HWP Fortnight {fIdx + 1}
              </div>
              <div className="text-xs font-medium tabular-nums">
                Week Commencing {format(blockStart, "d MMM yyyy")} — {format(blockEnd, "d MMM yyyy")}
              </div>
            </div>

            {/* Scrollable grid */}
            <div className="overflow-x-auto bg-white dark:bg-zinc-900">
              <table className="border-collapse text-[11px] min-w-full">
                <thead>
                  {/* Day row */}
                  <tr>
                    <th className="bg-zinc-900 text-white text-[10px] uppercase tracking-wider font-semibold px-3 py-1.5 border border-zinc-700 sticky left-0 z-20 min-w-[180px] text-left">
                      Officer
                    </th>
                    {block.map((d, i) => (
                      <th
                        key={`day-${i}`}
                        className={`text-[10px] uppercase font-bold px-1 py-1 border border-zinc-300 dark:border-zinc-700 text-center w-[58px] min-w-[58px] ${
                          isInRoster(d)
                            ? d.getDay() === 0 || d.getDay() === 6
                              ? "bg-zinc-200 dark:bg-zinc-800 text-zinc-800 dark:text-zinc-200"
                              : "bg-zinc-100 dark:bg-zinc-800/60 text-zinc-700 dark:text-zinc-300"
                            : "bg-zinc-50 dark:bg-zinc-900 text-zinc-300 dark:text-zinc-700"
                        }`}
                      >
                        {dayLabel(d)}
                      </th>
                    ))}
                    <th className="bg-zinc-900 text-white text-[9px] uppercase tracking-wider font-semibold px-2 py-1.5 border border-zinc-700 w-[60px] text-center">
                      # Rest
                    </th>
                  </tr>
                  {/* Date row */}
                  <tr>
                    <th className="bg-zinc-800 text-zinc-400 text-[10px] uppercase font-medium px-3 py-1 border border-zinc-700 sticky left-0 z-20 text-left">
                      Rank · Name · Badge
                    </th>
                    {block.map((d, i) => (
                      <th
                        key={`date-${i}`}
                        className={`text-[10px] tabular-nums font-medium px-1 py-1 border border-zinc-300 dark:border-zinc-700 text-center ${
                          isInRoster(d)
                            ? "bg-white dark:bg-zinc-900 text-zinc-700 dark:text-zinc-300"
                            : "bg-zinc-50 dark:bg-zinc-900 text-zinc-300 dark:text-zinc-700"
                        }`}
                      >
                        {dateLabel(d)}
                      </th>
                    ))}
                    <th className="bg-zinc-800 text-zinc-400 text-[9px] font-medium px-2 py-1 border border-zinc-700 text-center">
                      Days
                    </th>
                  </tr>
                  {/* Operations strip */}
                  {hasAnyOps && (
                    <tr data-testid={`police-ops-strip-${fIdx}`}>
                      <th className="bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300 text-[10px] uppercase font-semibold px-3 py-1 border border-zinc-300 dark:border-zinc-700 sticky left-0 z-20 text-left">
                        Operations
                      </th>
                      {block.map((d, i) => (
                        <td
                          key={`ops-${i}`}
                          className={`align-top px-0.5 py-0.5 border border-zinc-300 dark:border-zinc-700 ${
                            isInRoster(d)
                              ? "bg-white dark:bg-zinc-900"
                              : "bg-zinc-50 dark:bg-zinc-900/40"
                          }`}
                        >
                          {dedupedOps[i].map(({ shift }, j) => (
                            <div
                              key={`op-${i}-${j}`}
                              className="text-[8px] leading-tight font-bold uppercase truncate px-1 py-0.5 mb-0.5 rounded text-zinc-900 dark:text-zinc-100"
                              style={{
                                backgroundColor: (shift.color || "#52525B") + "55",
                                borderLeft: `2px solid ${shift.color || "#52525B"}`,
                              }}
                              title={shift.name}
                            >
                              {shift.name.slice(0, 8)}
                            </div>
                          ))}
                        </td>
                      ))}
                      <td className="bg-zinc-100 dark:bg-zinc-800 border border-zinc-300 dark:border-zinc-700"></td>
                    </tr>
                  )}
                </thead>
                <tbody>
                  {employees.map((emp) => {
                    let restCount = 0;

                    return (
                      <tr key={emp.employee_id} data-testid={`police-row-${emp.employee_id}`}>
                        {/* Name cell */}
                        <td className="bg-white dark:bg-zinc-900 border border-zinc-300 dark:border-zinc-700 px-3 py-1 sticky left-0 z-10 min-w-[180px]">
                          <div className="text-[12px] font-semibold text-zinc-900 dark:text-zinc-100 truncate">
                            {emp.name}
                          </div>
                          <div className="text-[9px] text-zinc-500 dark:text-zinc-500 tabular-nums">
                            #{emp.employee_id.split("_")[1]?.slice(0, 6) || emp.employee_id.slice(0, 6)}
                          </div>
                        </td>
                        {/* Day cells */}
                        {block.map((d, i) => {
                          const ds = format(d, "yyyy-MM-dd");
                          if (!isInRoster(d)) {
                            return (
                              <td
                                key={`c-${emp.employee_id}-${i}`}
                                className="bg-zinc-50 dark:bg-zinc-900/40 border border-zinc-300 dark:border-zinc-700"
                              ></td>
                            );
                          }
                          const onAL = onLeave(emp.employee_id, ds);
                          const ents = cellEntries(emp.employee_id, ds);
                          const { am, pm } = splitAmPm(ents);
                          const isWeekend = d.getDay() === 0 || d.getDay() === 6;
                          const noWork = !onAL && am.length === 0 && pm.length === 0;
                          if (noWork) restCount += 1;
                          const baseBg = isWeekend
                            ? "bg-zinc-50 dark:bg-zinc-900/60"
                            : "bg-white dark:bg-zinc-900";

                          return (
                            <td
                              key={`c-${emp.employee_id}-${i}`}
                              className={`border border-zinc-300 dark:border-zinc-700 align-top p-0 w-[58px]`}
                            >
                              {onAL ? (
                                <div
                                  className="h-[44px] flex flex-col items-center justify-center text-[10px] font-bold uppercase"
                                  style={{ backgroundColor: leaveColor + "AA", color: "#3b0764" }}
                                  title="Annual Leave"
                                  data-testid={`police-leave-${emp.employee_id}-${ds}`}
                                >
                                  A/L
                                </div>
                              ) : noWork ? (
                                <div
                                  className="h-[44px] flex items-center justify-center text-[10px] font-bold uppercase"
                                  style={{ backgroundColor: restAbbrevColor + "BB", color: "#78350f" }}
                                  title="Rest day"
                                  data-testid={`police-rest-${emp.employee_id}-${ds}`}
                                >
                                  REST
                                </div>
                              ) : (
                                <div className="grid grid-rows-2 h-[44px]">
                                  {/* AM half */}
                                  <div
                                    className={`flex items-center justify-center text-[9px] font-bold tabular-nums px-0.5 ${baseBg} ${
                                      am.length ? "" : "text-zinc-300 dark:text-zinc-700"
                                    }`}
                                    style={
                                      am.length
                                        ? {
                                            backgroundColor: halfBgColor(am),
                                            borderBottom: `1px dashed ${halfBorder(am)}40`,
                                            color: "#18181b",
                                          }
                                        : { borderBottom: "1px dashed #e5e7eb" }
                                    }
                                    title={halfTitle(am)}
                                  >
                                    {halfCellLabel(am) || "—"}
                                    {am.length > 0 && am[0].entry.is_locked && (
                                      <Lock className="w-2 h-2 ml-0.5" title="Locked — preserved on regenerate" />
                                    )}
                                  </div>
                                  {/* PM half */}
                                  <div
                                    className={`flex items-center justify-center text-[9px] font-bold tabular-nums px-0.5 ${baseBg} ${
                                      pm.length ? "" : "text-zinc-300 dark:text-zinc-700"
                                    }`}
                                    style={
                                      pm.length
                                        ? {
                                            backgroundColor: halfBgColor(pm),
                                            color: "#18181b",
                                          }
                                        : {}
                                    }
                                    title={halfTitle(pm)}
                                  >
                                    {halfCellLabel(pm) || "—"}
                                    {pm.length > 0 && pm[0].entry.is_locked && (
                                      <Lock className="w-2 h-2 ml-0.5" title="Locked — preserved on regenerate" />
                                    )}
                                  </div>
                                </div>
                              )}
                            </td>
                          );
                        })}
                        {/* Rest day count */}
                        <td
                          className="bg-rose-50 dark:bg-rose-900/20 border border-zinc-300 dark:border-zinc-700 text-center text-[12px] font-bold tabular-nums text-zinc-900 dark:text-zinc-100"
                          data-testid={`police-rest-count-${emp.employee_id}`}
                        >
                          {restCount}
                        </td>
                      </tr>
                    );
                  })}
                  {/* AM tally row */}
                  <tr>
                    <td className="bg-zinc-900 text-zinc-300 text-[10px] uppercase font-bold px-3 py-1 border border-zinc-700 sticky left-0 z-10">
                      AM
                    </td>
                    {block.map((d, i) => {
                      const ds = format(d, "yyyy-MM-dd");
                      if (!isInRoster(d)) {
                        return (
                          <td
                            key={`am-${i}`}
                            className="bg-zinc-50 dark:bg-zinc-900/40 border border-zinc-300 dark:border-zinc-700"
                          ></td>
                        );
                      }
                      const count = (roster.entries || []).filter((e) => {
                        if (e.date !== ds) return false;
                        const sh = shiftMap[e.shift_id];
                        if (!sh) return false;
                        return parseInt((sh.start_time || "00:00").split(":")[0], 10) < 12;
                      }).length;
                      return (
                        <td
                          key={`am-${i}`}
                          className="bg-emerald-50 dark:bg-emerald-900/20 border border-zinc-300 dark:border-zinc-700 text-center text-[11px] tabular-nums font-bold text-emerald-800 dark:text-emerald-300"
                        >
                          {count}
                        </td>
                      );
                    })}
                    <td className="bg-zinc-900 border border-zinc-700"></td>
                  </tr>
                  {/* PM tally row */}
                  <tr>
                    <td className="bg-zinc-900 text-zinc-300 text-[10px] uppercase font-bold px-3 py-1 border border-zinc-700 sticky left-0 z-10">
                      PM
                    </td>
                    {block.map((d, i) => {
                      const ds = format(d, "yyyy-MM-dd");
                      if (!isInRoster(d)) {
                        return (
                          <td
                            key={`pm-${i}`}
                            className="bg-zinc-50 dark:bg-zinc-900/40 border border-zinc-300 dark:border-zinc-700"
                          ></td>
                        );
                      }
                      const count = (roster.entries || []).filter((e) => {
                        if (e.date !== ds) return false;
                        const sh = shiftMap[e.shift_id];
                        if (!sh) return false;
                        return parseInt((sh.start_time || "00:00").split(":")[0], 10) >= 12;
                      }).length;
                      return (
                        <td
                          key={`pm-${i}`}
                          className="bg-amber-50 dark:bg-amber-900/20 border border-zinc-300 dark:border-zinc-700 text-center text-[11px] tabular-nums font-bold text-amber-800 dark:text-amber-300"
                        >
                          {count}
                        </td>
                      );
                    })}
                    <td className="bg-zinc-900 border border-zinc-700"></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
      {/* Legend */}
      <div className="flex flex-wrap items-center gap-3 text-[11px] text-zinc-600 dark:text-zinc-400 px-1" data-testid="police-legend">
        <span className="font-semibold uppercase tracking-wide text-[10px] text-zinc-500">Legend:</span>
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block w-3 h-3 rounded-sm" style={{ background: restAbbrevColor + "BB" }} /> REST
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block w-3 h-3 rounded-sm" style={{ background: leaveColor + "AA" }} /> A/L (Annual Leave)
        </span>
        {shifts.slice(0, 8).map((s) => (
          <span key={s.shift_id} className="inline-flex items-center gap-1.5">
            <span className="inline-block w-3 h-3 rounded-sm" style={{ background: (s.color || "#002FA7") + "33", borderLeft: `2px solid ${s.color || "#002FA7"}` }} />
            {s.name}
          </span>
        ))}
        <span className="text-zinc-400">· AM = shift start &lt; 12:00 · PM = shift start ≥ 12:00</span>
      </div>
    </div>
  );
};

export default PoliceRosterView;
